/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { DateTimeRange } from '@/components/ui'
import { AssetDropdownSingle } from '@/insights/components/AssetDropdownSingle'
import { CompanyDropdown } from '@/insights/components/CompanyDropdown'

import type { MigrationFilters } from './types'

const DigitsInput: React.FC<{ label: string; value: number; onChange: (n: number) => void; testId: string }> = ({
  label,
  value,
  onChange,
  testId,
}) => (
  <label className="form-control w-32">
    <span className="label-text text-sm">{label}</span>
    <input
      type="number"
      min={0}
      max={10}
      className="input input-bordered input-sm"
      value={value}
      data-testid={testId}
      onChange={(e) => {
        const n = Number(e.target.value)
        if (Number.isInteger(n) && n >= 0 && n <= 10) onChange(n)
      }}
    />
  </label>
)

interface Props {
  filters: MigrationFilters
  onChange: (patch: Partial<MigrationFilters>) => void
}

/**
 * Company (Fresh hierarchy — both back ends take Fresh ids), then the asset once a company is
 * chosen, and the wall-clock date range the insights take.
 */
export const MigrationFiltersBar: React.FC<Props> = ({ filters, onChange }) => (
  <div className="card bg-base-100 shadow" data-testid="migration-filters">
    <div className="card-body grid gap-4 md:grid-cols-3">
      <CompanyDropdown
        value={filters.companyId}
        onChange={(companyId) =>
          onChange({ companyId: typeof companyId === 'number' ? companyId : undefined, assetId: undefined })
        }
      />
      {filters.companyId ? (
        <AssetDropdownSingle
          key={filters.companyId}
          companyId={filters.companyId}
          asset={filters.assetId ? String(filters.assetId) : undefined}
          onChange={(assetId) => onChange({ assetId: assetId ? Number(assetId) : undefined })}
        />
      ) : (
        <div className="text-sm text-base-content/60">Choose a company to pick an asset</div>
      )}
      <DateTimeRange
        from={filters.from}
        to={filters.to}
        fromLabel="From"
        toLabel="To"
        onChange={({ from, to }) => onChange({ from, to })}
      />
      <div className="flex gap-4 md:col-span-3">
        <DigitsInput
          label="Lat/lng decimals"
          value={filters.coordDigits}
          onChange={(coordDigits) => onChange({ coordDigits })}
          testId="migration-coord-digits"
        />
        <DigitsInput
          label="Number decimals"
          value={filters.numberDigits}
          onChange={(numberDigits) => onChange({ numberDigits })}
          testId="migration-number-digits"
        />
        <p className="self-end pb-1 text-xs text-base-content/60">
          Numbers are rounded to these decimals on both sides before they are compared.
        </p>
      </div>
    </div>
  </div>
)
